import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Container,
  Image,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

import { getPublicMissionInfo } from "../../api/partnersApi";
import AmbientBlobs from "../../marketing/components/AmbientBlobs";
import "../../marketing/styles.css";
import "./landing-form.css";

const parseSkillsJson = (raw) => {
  if (!raw) return [];
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
};

const mergeUnique = (a, b) => {
  const out = [];
  const seen = new Set();
  for (const skill of [...(a || []), ...(b || [])]) {
    const key = (skill || "").toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(skill);
  }
  return out;
};

const formatDuration = (mission, t) => {
  if (!mission) return null;
  let base = null;
  if (mission.durationUnit === "months" && mission.durationMonths) {
    base = `${mission.durationMonths} ${t("landing.sections.months", "mois")}`;
  } else if (mission.durationDays) {
    base = `${mission.durationDays} ${t("landing.sections.days", "jours")}`;
  } else if (mission.durationMonths) {
    base = `${mission.durationMonths} ${t("landing.sections.months", "mois")}`;
  }
  if (!base) return null;
  if (mission.isRenewable) {
    return `${base} ${t("landing.sections.renewable", "(renouvelable)")}`;
  }
  return base;
};

const getContractTypes = (mission) => {
  if (!mission) return [];
  if (Array.isArray(mission.contractTypes) && mission.contractTypes.length > 0) {
    return mission.contractTypes;
  }
  return mission.contractType ? [mission.contractType] : [];
};

const formatSalary = (mission, t) => {
  if (!mission) return null;
  if (mission.salaireSelonProfil) {
    return t("landing.sections.selonProfil", "selon profil");
  }
  if (mission.salaireMin && mission.salaireMax) {
    return `${mission.salaireMin}€ – ${mission.salaireMax}€ / ${t("landing.sections.year", "an")}`;
  }
  if (mission.salaireMin) return `${mission.salaireMin}€ / ${t("landing.sections.year", "an")}+`;
  if (mission.salaireMax) return `≤ ${mission.salaireMax}€ / ${t("landing.sections.year", "an")}`;
  return null;
};

const formatTjm = (mission, t) => {
  if (!mission) return null;
  if (mission.tjmSelonProfil) {
    return t("landing.sections.selonProfil", "selon profil");
  }
  if (mission.tjmMin && mission.tjmMax) {
    return `${mission.tjmMin}€ – ${mission.tjmMax}€ / ${t("landing.sections.day", "jour")}`;
  }
  if (mission.tjmMin) return `${mission.tjmMin}€+ / ${t("landing.sections.day", "jour")}`;
  if (mission.tjmMax) return `≤ ${mission.tjmMax}€ / ${t("landing.sections.day", "jour")}`;
  return null;
};

const parseGeneratedSections = (raw) => {
  if (!raw) return {};
  try {
    const v = typeof raw === "string" ? JSON.parse(raw) : raw;
    return v && typeof v === "object" ? v : {};
  } catch {
    return {};
  }
};

// Split a prose section ("intro line(s)" + "- bullet" lines) into parts.
const splitProse = (text) => {
  const intro = [];
  const bullets = [];
  for (const line of String(text || "").split("\n")) {
    const s = line.trim();
    if (!s) continue;
    if (/^[-•*]/.test(s)) bullets.push(s.replace(/^[-•*]\s*/, "").trim());
    else intro.push(s);
  }
  return { intro, bullets };
};

const Section = ({ testId, title, children }) => (
  <Box className="lf-section" data-testid={testId}>
    <Text as="h2" className="lf-section-title" sx={{ marginBottom: "10px !important" }}>
      {title}
    </Text>
    {children}
  </Box>
);

const BulletList = ({ items }) => (
  <ul className="lf-bullets">
    {items.map((s, i) => (
      <li key={i}>{s}</li>
    ))}
  </ul>
);

// Renders the AI-rephrased prose when present (intro paragraphs + bullets),
// otherwise falls back to the raw skill bullet list. Renders nothing if both empty.
const ProseSection = ({ testId, title, prose, fallbackItems }) => {
  const text = (prose || "").trim();
  if (text) {
    const { intro, bullets } = splitProse(text);
    return (
      <Section testId={testId} title={title}>
        {intro.map((p, i) => (
          <Text key={i} className="lf-body" sx={{ marginBottom: "8px !important" }}>
            {p}
          </Text>
        ))}
        {bullets.length > 0 && <BulletList items={bullets} />}
      </Section>
    );
  }
  if (fallbackItems && fallbackItems.length > 0) {
    return (
      <Section testId={testId} title={title}>
        <BulletList items={fallbackItems} />
      </Section>
    );
  }
  return null;
};

const LandingWelcome = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("public");
  const [loading, setLoading] = useState(true);
  const [mission, setMission] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.body.classList.add("marketing-page");
    document.body.classList.add("is-landing-welcome");
    return () => {
      document.body.classList.remove("marketing-page");
      document.body.classList.remove("is-landing-welcome");
    };
  }, []);

  useEffect(() => {
    const explicit = window.localStorage.getItem("portalLanguage");
    if (!explicit) {
      i18n.changeLanguage("fr");
    }
  }, [i18n]);

  useEffect(() => {
    const fetchMission = async () => {
      try {
        setLoading(true);
        const data = await getPublicMissionInfo(token);
        setMission(data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError(t("landing.welcome.missionNotFound"));
        } else {
          setError(t("landing.welcome.loadFailed"));
        }
      } finally {
        setLoading(false);
      }
    };
    fetchMission();
  }, [token, t]);

  const handleApply = () => {
    navigate(`/partners/apply/${token}/form`);
  };

  // Header shows the client company's own logo when available; otherwise it is
  // intentionally empty (the generic ASAP Hub mark has been removed). Platform
  // attribution lives only in the "Powered by ASAP Hub" footer.
  const renderHeader = (logoUrl, companyName) => (
    <Box
      as="header"
      className="lf-header"
      display="flex"
      alignItems="center"
      justifyContent="center"
      pt={{ base: 6, md: 8 }}
      pb={{ base: 4, md: 6 }}
    >
      {logoUrl ? (
        <Image
          src={logoUrl}
          alt={companyName || ""}
          className="lf-header-logo"
          data-testid="landing-company-logo"
        />
      ) : null}
    </Box>
  );

  const footer = (
    <Box as="footer" className="lf-footer" data-testid="landing-footer">
      <Text as="span" className="lf-footer-text" sx={{ marginBottom: "0 !important" }}>
        {t("landing.footer.poweredBy", "Powered by")} ASAP Hub
      </Text>
    </Box>
  );

  if (loading) {
    return (
      <Box className="lf-shell" position="relative">
        <AmbientBlobs />
        {renderHeader(null)}
        <Container maxW="container.md" pb={16}>
          <Box className="lf-card lf-card-state">
            <VStack spacing={4}>
              <Spinner size="xl" color="#79f5c7" thickness="4px" />
              <Text className="lf-muted" sx={{ marginBottom: "0 !important" }}>
                {t("landing.welcome.loading")}
              </Text>
            </VStack>
          </Box>
        </Container>
        {footer}
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="lf-shell" position="relative">
        <AmbientBlobs />
        {renderHeader(null)}
        <Container maxW="container.md" pb={16}>
          <Box className="lf-card lf-card-state" textAlign="center">
            <Text
              as="h1"
              className="lf-title"
              fontSize="24px"
              sx={{ marginBottom: "8px !important" }}
            >
              {t("landing.welcome.errorTitle")}
            </Text>
            <Text className="lf-muted" sx={{ marginBottom: "0 !important" }}>
              {error}
            </Text>
          </Box>
        </Container>
        {footer}
      </Box>
    );
  }

  if (!mission?.isOpen) {
    return (
      <Box className="lf-shell" position="relative">
        <AmbientBlobs />
        {renderHeader(null)}
        <Container maxW="container.md" pb={16}>
          <Box className="lf-card lf-card-state" textAlign="center">
            <Text
              as="h1"
              className="lf-title"
              fontSize="24px"
              sx={{ marginBottom: "8px !important" }}
            >
              {t("landing.welcome.closedTitle")}
            </Text>
            <Text className="lf-muted" sx={{ marginBottom: "0 !important" }}>
              {t("landing.welcome.closedMessage")}
            </Text>
          </Box>
        </Container>
        {footer}
      </Box>
    );
  }

  const requiredSkills = parseSkillsJson(mission?.requiredSkills);
  const optionalSkills = parseSkillsJson(mission?.optionalSkills);
  const skillsTechnicalList = parseSkillsJson(mission?.skillsTechnical);
  const skillsFunctionalList = parseSkillsJson(mission?.skillsFunctional);
  const skillsBusinessList = parseSkillsJson(mission?.skillsBusiness);

  const competencesRequises = mergeUnique(requiredSkills, skillsTechnicalList);
  const profilRecherche = mergeUnique(skillsBusinessList, optionalSkills);

  const pratiquesRows = [];
  if (mission?.location) {
    pratiquesRows.push([t("landing.sections.locationLabel", "Lieu"), mission.location]);
  }
  const startLabel = mission?.startAsap ? t("landing.sections.asap", "ASAP") : mission?.startDate;
  if (startLabel) {
    pratiquesRows.push([t("landing.sections.startDateLabel", "Démarrage"), startLabel]);
  }
  const durationLabel = formatDuration(mission, t);
  if (durationLabel) {
    pratiquesRows.push([t("landing.sections.durationLabel", "Durée"), durationLabel]);
  }
  const contractTypes = getContractTypes(mission);
  if (contractTypes.length > 0) {
    pratiquesRows.push([
      t("landing.sections.contractLabel", "Contrat"),
      contractTypes
        .map((c) => t(`landing.sections.contractTypes.${c}`, c))
        .join(", "),
    ]);
  }
  const hasCdi = contractTypes.includes("cdi");
  const hasTjm = contractTypes.some((c) => c !== "cdi");
  // With no recognised contract type, fall back to showing whichever rate exists.
  const showSalary = hasCdi || contractTypes.length === 0;
  const showTjm = hasTjm || contractTypes.length === 0;
  const salaryLabel = showSalary ? formatSalary(mission, t) : null;
  if (salaryLabel) {
    pratiquesRows.push([t("landing.sections.salaryLabel", "Salaire"), salaryLabel]);
  }
  const tjmLabel = showTjm ? formatTjm(mission, t) : null;
  if (tjmLabel) {
    pratiquesRows.push([t("landing.sections.tjmLabel", "TJM"), tjmLabel]);
  }

  const purposeLabel =
    mission.purpose === "contact_pool"
      ? t("landing.welcome.purposePool")
      : mission.purpose === "propose_assignment"
      ? t("landing.welcome.purposeAssignment")
      : t("landing.welcome.purposeBench");

  // AI-rephrased prose (replaces the raw skill bullets when present).
  const generated = parseGeneratedSections(mission?.generatedSections);

  // Subtitle / meta line under the title — mirrors the email "subtitle 2".
  const subtitleParts = [];
  if (durationLabel) subtitleParts.push(`${t("landing.sections.durationLabel", "Durée")} : ${durationLabel}`);
  if (startLabel) subtitleParts.push(`${t("landing.sections.startDateLabel", "Démarrage")} : ${startLabel}`);
  if (mission?.location) subtitleParts.push(`${t("landing.sections.locationLabel", "Lieu")} : ${mission.location}`);
  // Show both the daily rate and the annual salary when both exist (like the email).
  if (tjmLabel) subtitleParts.push(`${t("landing.sections.tjmLabel", "TJM")} : ${tjmLabel}`);
  if (salaryLabel) subtitleParts.push(`${t("landing.sections.salaryLabel", "Salaire")} : ${salaryLabel}`);

  return (
    <Box className="lf-shell" position="relative">
      <AmbientBlobs />
      {renderHeader(mission?.companyLogoUrl, mission?.companyName)}

      <Container maxW="container.md" pb={{ base: 12, md: 20 }}>
        <Box className="lf-card" data-testid="landing-welcome-card">
          <Box textAlign="center" mb={{ base: 6, md: 8 }}>
            <Text className="lf-kicker" sx={{ marginBottom: "8px !important" }}>
              {purposeLabel}
            </Text>
            <Text
              as="h1"
              className="lf-title"
              sx={{ marginBottom: "0 !important" }}
            >
              {mission?.title}
            </Text>
            {subtitleParts.length > 0 && (
              <Text
                className="lf-subtitle"
                data-testid="landing-subtitle"
                sx={{ marginBottom: "0 !important" }}
              >
                {subtitleParts.join("  |  ")}
              </Text>
            )}
          </Box>

          {mission?.description && (
            <Section testId="section-contexte" title={t("landing.sections.contexte", "Contexte")}>
              <Text className="lf-body" whiteSpace="pre-wrap" sx={{ marginBottom: "0 !important" }}>
                {mission.description}
              </Text>
            </Section>
          )}

          <ProseSection
            testId="section-missions-roles"
            title={t("landing.sections.missionsRoles", "Missions, Rôles et responsabilités")}
            prose={generated.responsibilities}
            fallbackItems={skillsFunctionalList}
          />

          <ProseSection
            testId="section-competences-requises"
            title={t("landing.sections.competencesRequises", "Compétences requises")}
            prose={generated.requiredSkills}
            fallbackItems={competencesRequises}
          />

          <ProseSection
            testId="section-profil-recherche"
            title={t("landing.sections.profilRecherche", "Profil recherché")}
            prose={generated.candidateProfile}
            fallbackItems={profilRecherche}
          />

          {pratiquesRows.length > 0 && (
            <Section
              testId="section-informations-pratiques"
              title={t("landing.sections.informationsPratiques", "Informations pratiques")}
            >
              <ul className="lf-bullets">
                {pratiquesRows.map(([k, v], i) => (
                  <li key={i}>
                    <Text as="span" fontWeight="800" color="#e6efff" sx={{ marginBottom: "0 !important" }}>
                      {k} :
                    </Text>{" "}
                    {v}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          <Box mt={{ base: 6, md: 8 }}>
            <button
              type="button"
              onClick={handleApply}
              className="mk-btn mk-btn-primary lf-submit"
              data-testid="landing-welcome-apply"
            >
              {t("landing.welcome.applyNow", "Postuler maintenant")}
            </button>
          </Box>
        </Box>
      </Container>
      {footer}
    </Box>
  );
};

export default LandingWelcome;
